import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useLanguage } from '@/i18n/LanguageContext'
import { navItems } from './nav'
import { resolveNavHref } from './resolveNavHref'
import { GlowRouterLink } from '@/components/ui/GlowButton'
import { LogoMark } from '@/components/ui/LogoMark'

/**
 * Mobile top bar (logo + hamburger) + full-screen overlay nav.
 * Same anchors/order as DesktopNav — single source of truth in nav.ts.
 */
export function MobileNav() {
  const [open, setOpen] = useState(false)
  const { lang, setLang, t } = useLanguage()

  // While the full-screen overlay is open: lock page scroll behind it and
  // let Escape close it (it's role="dialog" aria-modal, so it should behave like one).
  useEffect(() => {
    if (!open) return
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prevOverflow
      window.removeEventListener('keydown', onKey)
    }
  }, [open])
  return (
    <>
      <div
        className="flex lg:hidden fixed top-0 inset-x-0 z-50 items-center justify-between
                   px-4 py-3 bg-background/80 backdrop-blur-xl border-b border-white/10"
      >
        <Link
          to={resolveNavHref('#top', lang)}
          className="flex items-center gap-2 font-serif text-lg text-foreground"
          onClick={() => setOpen(false)}
        >
          <LogoMark className="h-6 w-6 shrink-0" />
          EA Studio
        </Link>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setLang(lang === 'bs' ? 'en' : 'bs')}
            className="flex h-9 items-center justify-center rounded-pill border border-white/15 px-3 text-xs font-bold uppercase tracking-wide text-foreground-muted transition-colors hover:border-accent-fire/50 hover:text-accent-fire"
            aria-label="Switch language"
          >
            {lang === 'bs' ? 'EN' : 'BS'}
          </button>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls="mobile-nav-overlay"
            aria-label={open ? 'Close menu' : 'Open menu'}
            className="flex h-11 w-11 items-center justify-center rounded-lg text-foreground"
          >
            <span className="relative block h-4 w-6">
              <span
                className={`absolute left-0 top-0 h-0.5 w-6 bg-current transition-transform duration-fast ${
                  open ? 'translate-y-[7px] rotate-45' : ''
                }`}
              />
              <span
                className={`absolute left-0 top-[7px] h-0.5 w-6 bg-current transition-opacity duration-fast ${
                  open ? 'opacity-0' : ''
                }`}
              />
              <span
                className={`absolute left-0 top-[14px] h-0.5 w-6 bg-current transition-transform duration-fast ${
                  open ? '-translate-y-[7px] -rotate-45' : ''
                }`}
              />
            </span>
          </button>
        </div>
      </div>

      {open && (
        <div
          id="mobile-nav-overlay"
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-40 flex lg:hidden flex-col bg-background/95 backdrop-blur-2xl pt-20 px-6"
        >
          <p className="font-mono text-xs uppercase tracking-widest text-foreground-muted mb-4">
            {t('mnav-tagline')}
          </p>

          <ul className="flex flex-col gap-1">
            {navItems.map((item) => (
              <li key={item.href}>
                <Link
                  to={resolveNavHref(item.href, lang)}
                  onClick={() => setOpen(false)}
                  className="block py-3 text-lg text-foreground border-b border-white/5"
                >
                  {t(item.labelKey)}
                </Link>
              </li>
            ))}
          </ul>

          <div className="mt-auto pb-10 flex flex-col gap-4">
            <GlowRouterLink to={resolveNavHref('/kontakt', lang)} onClick={() => setOpen(false)} className="w-full">
              {t('nav-cta')}
            </GlowRouterLink>
          </div>
        </div>
      )}
    </>
  )
}
